import moment from 'moment';
import * as XLSX from 'xlsx';
import { formatCell } from './reporting.format';
import { ReportChartData } from './reporting.chartData';

// A plain decimal with no leading zero (so '000123' and long ids stay text) that fits in a
// double without losing digits — safe to hand Excel as a number it can sum.
const PLAIN_NUMBER = /^-?(0|[1-9]\d{0,14})(\.\d+)?$/;

const toExcelCell = (value: any, columnName: string): string | number => {
  const shown = formatCell(value, columnName);
  if (shown === null || shown === undefined || shown === '') {
    return '';
  }
  if (typeof shown === 'number') {
    return shown;
  }
  return PLAIN_NUMBER.test(String(shown)) ? Number(shown) : String(shown);
};

/** Header row of column names, then every row formatted exactly as the table shows it. */
export const buildExportRows = (data: ReportChartData): (string | number)[][] => [
  data.columns.map((c) => c.name),
  ...data.rows.map((row) => data.columns.map((c, i) => toExcelCell(row[i], c.name))),
];

/** Excel rejects sheet names containing : \ / ? * [ ] or longer than 31 characters. */
export const exportSheetName = (title: string): string => title.replace(/[:\\/?*[\]]/g, '').trim().slice(0, 31) || 'Report';

const fileSafe = (text: string) => text.replace(/[\\/:*?"<>|]/g, '').trim();

export const exportFileName = (reportTitle: string, chartTitle: string, day: moment.Moment): string =>
  `${fileSafe(reportTitle)} - ${fileSafe(chartTitle)} - ${day.format('YYYY-MM-DD')}.xlsx`;

export const buildExportWorkbook = (data: ReportChartData, chartTitle: string): XLSX.WorkBook => {
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, XLSX.utils.aoa_to_sheet(buildExportRows(data)), exportSheetName(chartTitle));
  return workbook;
};

/** Builds the .xlsx in the browser and hands it to the user as a download. */
export const downloadChartExcel = (data: ReportChartData, reportTitle: string, chartTitle: string): void => {
  XLSX.writeFile(buildExportWorkbook(data, chartTitle), exportFileName(reportTitle, chartTitle, moment()));
};
